import React, { useEffect, useState } from "react";
import Aurora from "./ui/Aurora";
import PawField from "./ui/PawField";
import MagneticButton from "./ui/MagneticButton";
import Reveal from "./ui/Reveal";
import useTilt from "../hooks/useTilt";

const WHATSAPP_URL =
  "https://wa.me/5516997502577?text=Ol%C3%A1%2C%20gostaria%20de%20saber%20mais%20sobre%20o%20PawSuite.";

const ROTATING = ["inteligência", "clareza", "controle", "tranquilidade"];

/** Troca a palavra com fade + blur, mantendo a largura reservada. */
const RotatingWord = () => {
  const [index, setIndex] = useState(0);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    const cycle = setInterval(() => {
      setLeaving(true);
      setTimeout(() => {
        setIndex((i) => (i + 1) % ROTATING.length);
        setLeaving(false);
      }, 420);
    }, 2600);
    return () => clearInterval(cycle);
  }, []);

  return (
    <span className="relative inline-grid align-bottom">
      {/* Fantasma invisível: reserva a maior largura e evita "pulos" no layout */}
      <span
        aria-hidden="true"
        className="invisible col-start-1 row-start-1 whitespace-nowrap"
      >
        {ROTATING.reduce((a, b) => (a.length > b.length ? a : b))}
      </span>
      <span
        className={`text-gradient col-start-1 row-start-1 whitespace-nowrap transition-all duration-500 ${
          leaving
            ? "translate-y-2 opacity-0 blur-sm"
            : "translate-y-0 opacity-100 blur-0"
        }`}
        style={{ transitionTimingFunction: "cubic-bezier(.16,1,.3,1)" }}
      >
        {ROTATING[index]}
      </span>
    </span>
  );
};

/** Cartão flutuante que orbita o mockup. */
const FloatCard = ({ className, delay = 0, children }) => (
  <div
    className={`glass absolute hidden rounded-2xl px-4 py-3 shadow-lift animate-float-soft lg:block ${className}`}
    style={{ animationDelay: `${delay}s` }}
  >
    {children}
  </div>
);

const Hero = () => {
  const tilt = useTilt({ max: 7, scale: 1.015 });

  return (
    <header
      id="top"
      className="grain relative overflow-hidden pb-24 pt-36 lg:pb-32 lg:pt-44"
    >
      <Aurora />
      <PawField count={10} tone="rgba(15,31,61,.055)" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-6 lg:grid-cols-[1.05fr_1fr] lg:gap-12">
        {/* ---------------- Copy ---------------- */}
        <div>
          <Reveal delay={0}>
            <a
              href="#tour"
              className="group mb-8 inline-flex items-center gap-3 rounded-full border border-midnight/10 bg-white/70 py-1.5 pl-1.5 pr-4 text-sm backdrop-blur-md transition-all duration-300 hover:border-tealVitality/40 hover:shadow-glow"
            >
              <span className="flex items-center gap-1.5 rounded-full bg-tealVitality px-3 py-1 text-xs font-bold uppercase tracking-wider text-white">
                Novo
              </span>
              <span className="font-medium text-midnight/70">
                Portal do Tutor com fotos do banho
              </span>
              <svg
                className="h-4 w-4 text-midnight/35 transition-transform duration-300 group-hover:translate-x-1"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5" />
              </svg>
            </a>
          </Reveal>

          <Reveal delay={90}>
            <h1 className="mb-7 font-display text-[clamp(2.75rem,6.4vw,4.75rem)] font-extrabold leading-[1.03] tracking-[-0.035em] text-midnight">
              Gerencie com <RotatingWord />.
              <br />
              Cuide com{" "}
              <span className="underline-sketch text-pawOrange">amor</span>.
            </h1>
          </Reveal>

          <Reveal delay={200}>
            <p className="mb-10 max-w-xl text-lg leading-relaxed text-midnight/60 lg:text-xl">
              A plataforma que organiza agenda, prontuário, banho &amp; tosa e
              caixa em um só lugar — para você voltar a passar o dia com os pets,
              não com planilhas.
            </p>
          </Reveal>

          <Reveal delay={300}>
            <div className="flex flex-col gap-4 sm:flex-row">
              <MagneticButton
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group rounded-2xl bg-tealVitality px-8 py-4 text-lg font-bold text-white shadow-glow transition-shadow duration-300 hover:shadow-[0_0_0_1px_rgba(0,178,169,.3),0_28px_70px_-15px_rgba(0,178,169,.55)]"
              >
                Começar Agora
                <svg
                  className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </MagneticButton>

              <MagneticButton
                href="#tour"
                strength={0.2}
                className="group rounded-2xl border border-midnight/12 bg-white/60 px-8 py-4 text-lg font-bold text-midnight/75 backdrop-blur-md transition-all duration-300 hover:border-midnight/25 hover:bg-white hover:text-midnight"
              >
                <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-midnight/5 transition-colors duration-300 group-hover:bg-tealVitality/15">
                  <span className="absolute inset-0 rounded-full bg-tealVitality/30 animate-pulse-ring" />
                  <svg
                    className="relative h-4 w-4 translate-x-[1px] text-tealVitality"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M8 5.14v13.72L19 12 8 5.14z" />
                  </svg>
                </span>
                Ver o sistema
              </MagneticButton>
            </div>
          </Reveal>

          <Reveal delay={420}>
            <div className="mt-12 flex flex-wrap items-center gap-x-7 gap-y-3">
              {[
                "Sem taxa de instalação",
                "Cancele quando quiser",
                "Suporte humano",
              ].map((item) => (
                <span
                  key={item}
                  className="flex items-center gap-2 text-sm font-medium text-midnight/45"
                >
                  <svg
                    className="h-4 w-4 shrink-0 text-tealVitality"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  {item}
                </span>
              ))}
            </div>
          </Reveal>
        </div>

        {/* ---------------- Mockup ---------------- */}
        <Reveal variant="scale" delay={220} className="relative">
          <div
            className="tilt-scene relative"
            onMouseMove={tilt.onMouseMove}
            onMouseLeave={tilt.onMouseLeave}
          >
            <div ref={tilt.ref} className="tilt-target relative">
              {/* Halo */}
              <div className="absolute left-1/2 top-1/2 -z-10 h-[125%] w-[125%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-tr from-tealVitality/30 via-pawOrange/20 to-transparent blur-3xl" />

              {/* Moldura do app */}
              <div className="relative rounded-[28px] border border-white/70 bg-white/80 p-2.5 shadow-[0_40px_90px_-30px_rgba(15,31,61,.45)] backdrop-blur-xl">
                <div className="flex items-center gap-1.5 px-3 pb-2.5 pt-1">
                  <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" />
                  <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" />
                  <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" />
                  <span className="ml-3 truncate rounded-md bg-midnight/[.05] px-2.5 py-1 text-[11px] font-medium text-midnight/40">
                    app.pawsuite.com.br/agenda
                  </span>
                </div>
                <img
                  src="/tela_principal.png"
                  alt="Agenda do dia no PawSuite, com banhos e tosas organizados por horário"
                  className="w-full rounded-[18px] shadow-inner"
                  loading="eager"
                />
              </div>

              {/* Cartões orbitando */}
              <FloatCard className="-left-12 top-24" delay={0.6}>
                <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-midnight/40">
                  Faturamento hoje
                </p>
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-2xl font-extrabold text-midnight">
                    R$ 227
                  </span>
                  <span className="flex items-center gap-0.5 text-xs font-bold text-emerald-500">
                    <svg
                      className="h-3.5 w-3.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                      />
                    </svg>
                    18%
                  </span>
                </div>
              </FloatCard>

              <FloatCard className="-right-8 bottom-20" delay={1.7}>
                <div className="flex items-center gap-3">
                  <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-tealVitality/12 text-lg">
                    🐕
                    <span className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-white bg-emerald-500" />
                  </span>
                  <div>
                    <p className="text-sm font-bold leading-tight text-midnight">
                      Rex terminou o banho
                    </p>
                    <p className="text-xs text-midnight/45">
                      Foto enviada ao tutor · agora
                    </p>
                  </div>
                </div>
              </FloatCard>

              <FloatCard className="-right-4 top-8" delay={1.1}>
                <div className="flex items-center gap-2.5">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-pawOrange/15 text-pawOrange">
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 17h5l-1.4-1.4A2 2 0 0118 14.2V11a6 6 0 00-4-5.7V5a2 2 0 10-4 0v.3C7.7 6.2 6 8.4 6 11v3.2c0 .5-.2 1-.6 1.4L4 17h5m6 0v1a3 3 0 11-6 0v-1"
                      />
                    </svg>
                  </span>
                  <p className="text-xs font-semibold text-midnight/70">
                    3 vacinas vencem
                    <br />
                    <span className="text-midnight/45">nesta semana</span>
                  </p>
                </div>
              </FloatCard>
            </div>
          </div>
        </Reveal>
      </div>

      {/* Indicador de rolagem */}
      <div className="pointer-events-none relative mx-auto mt-20 hidden w-fit lg:block">
        <div className="flex flex-col items-center gap-3 text-midnight/30">
          <span className="text-[10px] font-bold uppercase tracking-[0.3em]">
            Role para explorar
          </span>
          <span className="flex h-10 w-6 justify-center rounded-full border-2 border-current pt-2">
            <span className="h-1.5 w-1 animate-bounce rounded-full bg-current" />
          </span>
        </div>
      </div>
    </header>
  );
};

export default Hero;
